/* import __COLOCATED_TEMPLATE__ from './lsg-invite-to-posting-modal.hbs'; */
import { computed } from '@ember/object';
import { readOnly } from '@ember/object/computed';
import { A } from '@ember/array';
import { inject as service } from '@ember/service';
import LSGModalBase from './lsg-modal-dialog-base';
import { closeModal } from "sh-www/utils/modal-controls";
import { handleFormErrorOrModal } from 'sh-www/utils/handles-errors';
import { htmlEntitiesDecode } from "../../../utils/misc";

/**
 * custom error elements for the form.
 *
 * @type {string[]}
 */
const FORM_ERRORS = [
  'message'
];

export default LSGModalBase.extend({
  /**
   * Register services
   */
  assignmentCommandService: service('commands/assignment-commands'),
  router: service(),
  session: service(),
  store: service(),

  currentUser: readOnly('session.currentUser'),

  /**
   * @inheritDoc
   */
  propertyDefaults: {
    /**
     * @param {Function}      Optional callback to do something after the invite succeeds.
     */
    onInviteSucceeded: function() {},

    /**
     * @param {User|null}   The user you want to invite to a posting.
     */
    userToInvite: null
  },

  /**
   * @inheritDoc
   */
  stateDefaults: {
    /**
     * @type {Boolean}
     */
    isInvitingUser: false,

    /**
     * @type {String}
     */
    message: ''
  },

  /**
   * @type {Ember.MutableArray}   The array of assignments for this user's company.
   */
  _assignments: A(),

  /**
   * List of options of postings for the dropdown select.
   *
   * @see lsg-select for format
   */
  _availablePostingsOptions: computed('_assignments.length', '_hasExactlyOnePosting', function() {
    let options = A(),
      assignments = this.get('_assignments');

    assignments.forEach(assignment => {
      options.pushObject({
        title: htmlEntitiesDecode(assignment.get('title')),
        value: assignment.get('id'),
      });
    });

    return options;
  }),

  /**
   * The first assignment in the list of assignments
   *
   * @type {Assignment|null}
   */
  _firstAssignment: computed('_assignments.length', function() {
    return this.get('_assignments').objectAt(0);
  }),

  /**
   * @type {Boolean}    True if we have assignemnts
   */
  _hasAssignments: computed('_assignments.length', function() {
    return this.get('_assignments.length');
  }),

  /**
   * @type {Boolean}    True if we have exactly one assignment
   */
  _hasExactlyOnePosting: computed('_assignments.length', function() {
    return this.get('_assignments.length') === 1;
  }),

  /**
   * @type {Boolean}    True when we're loading the assignments
   */
  _isLoadingAssignments: false,

  /**
   * @type {String|null}      The id of the posting the user has selected or null if nothing selected.
   */
  _postingSelectedValue: null,

  /**
   * @inheritDoc
   */
  _onClose() {
    this.set('_postingSelectedValue', null);
    this.set('_assignments', A());
  },

  /**
   * @inheritDoc
   */
  _onInitialization() {
    this.set('_isLoadingAssignments', true);
    this.set('errors', {});
    this.get('assignmentCommandService').loadPostingsForUser(this.get('currentUser')).then(assignments => {
      this.set('_assignments', assignments);

      if (assignments.get('length') >= 1) {
        //if we have at least one assignment, select it by default.
        this.set('_postingSelectedValue', assignments.objectAt(0).get('id'));
      }
    }).finally(() => this.set('_isLoadingAssignments', false));
  },

  actions: {
    closeModal() {
      closeModal(this);
    },

    /**
     * When the user confirms the invite.
     */
    onInviteConfirmed: function() {
      this.set('errors', {});
      this.set('state.isInvitingUser', true);

      let selectedAssignment = this.get('store').peekRecord('assignment', this.get('_postingSelectedValue')),
        userToInvite = this.get('model.userToInvite'),
        message = this.get('state.message');

      let callbackSuccess = this.get('model.onInviteSucceeded');
      this.get('assignmentCommandService').inviteToAssignment([userToInvite], selectedAssignment, message, false).then(() => {
        callbackSuccess();
        closeModal(this);
        this.set('state.isInvitingUser', false);
        this.set('')
      }).catch(e => {
        handleFormErrorOrModal(
          this,
          e,
          FORM_ERRORS,
          true,
          'There was an error inviting the user(s) to the posting. Please refresh and try again.',
          'onInviteConfirmed -- error inviting user ids to assignment. ' + userToInvite.get('id'));

        this.set('state.isInvitingUser', false);
      });
    },
  }
});
